import axios from 'axios'
import { getData, storeData, clearAll } from './asyncStore'
import { base_url, auth } from './endpoints'

export const api = axios.create({
    baseURL: base_url,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
})

const getProblem = (status) => {
    if (!status) return 'UNKNOWN_ERROR'
    if (status >= 200 && status <= 299) return null
    if (status >= 400 && status <= 499) return 'CLIENT_ERROR'
    if (status >= 500 && status <= 599) return 'SERVER_ERROR'
    return 'UNKNOWN_ERROR'
}

const forceLogout = () => {
    clearAll()
    window.location.href = "/#/login"
}

// the access token is short-lived (24h) by design, so it expiring mid-session is the normal case,
// not an error - only the refresh token (30d) expiring should actually force a logout. Multiple
// requests can hit 401 around the same time, so the in-flight refresh is shared instead of firing
// one refresh-tokens call per failed request.
let refreshPromise = null
const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            try {
                const tokens = await getData("@tokens")
                const refreshToken = tokens?.refresh?.token
                if (!refreshToken) return null
                const response = await axios.post(`${base_url}${auth.refreshTokens}`, { refreshToken })
                const newTokens = response.data
                if (newTokens?.access?.token) {
                    await storeData("@tokens", newTokens)
                    return newTokens
                }
                return null
            } catch (e) {
                return null
            } finally {
                refreshPromise = null
            }
        })()
    }
    return refreshPromise
}

const hit = async (endpoint = "", method = "GET", body = {}, axiosConfig = {}, _isRetry = false) => {
    const tokens = await getData("@tokens");
    const accessToken = tokens?.access
    if (accessToken) {
        api.defaults.headers.common['Authorization'] = `bearer ${accessToken.token}`;
    }

    try {
        let response
        const lm = method.toLowerCase()
        if (lm === 'get' || lm === 'delete') {
            response = await api[lm](endpoint, { params: body, ...axiosConfig })
        } else {
            response = await api[lm](endpoint, body, axiosConfig)
        }
        return { err: false, data: response.data, status: response.status }
    } catch (error) {
        if (axios.isCancel(error)) {
            return { err: true, aborted: true, msg: 'Request cancelled', status: null }
        }
        const status = error?.response?.status ?? null
        const problem = getProblem(status)
        const onLoginOrLanding = window.location.hash.startsWith("#/login") || window.location.hash.startsWith("#/landing") || window.location.hash === "" || window.location.hash === "#/"
        if (status === 401 && !onLoginOrLanding) {
            if (!_isRetry) {
                const newTokens = await refreshAccessToken()
                if (newTokens?.access?.token) {
                    return hit(endpoint, method, body, axiosConfig, true)
                }
            }
            // no refresh token, or it's expired/invalid too - session is genuinely over
            forceLogout()
            return { err: true, msg: 'Session expired', status }
        }
        if (problem === 'CLIENT_ERROR') {
            return { err: true, msg: error?.response?.data?.message ?? 'Client error', status }
        }
        if (problem === 'SERVER_ERROR') {
            return { err: true, msg: 'SERVER_ERROR', status }
        }
        return { err: true, msg: error?.message ?? 'UNKNOWN_ERROR', status }
    }
}

export default hit;